import {
  SOURCE_LANGUAGES,
  TARGET_LANGUAGES,
  CEFR_LEVELS,
  PACE_TIERS,
  TOPICS,
  LEARNING_REASONS,
  SITUATIONS,
  LEARNING_FOCUS,
  estimateGenerationCost,
  getBlockCount,
  getEstimatedPageCount,
} from '@ellie/shared';
import type {
  SourceLanguageCode,
  TargetLanguageCode,
  CefrLevelCode,
  PaceCode,
  TopicCode,
  LearningReasonCode,
  SituationCode,
  LearningFocusCode,
} from '@ellie/shared';
import { useStudioStore } from '@/stores/useStudioStore';
import { ProviderSelector } from '@/components/studio/ProviderSelector';
import { Loader2, Sparkles } from 'lucide-react';

export function ProfileForm() {
  const { profile, setProfile, generating, generate, provider, model } = useStudioStore();

  const costEstimate = estimateGenerationCost(profile.pace, { provider, model });
  const blockCount = getBlockCount(profile.pace);
  const pageCount = getEstimatedPageCount(profile.pace);

  return (
    <div className="space-y-5">
      {/* Display name */}
      <Field label="Learner name">
        <input
          type="text"
          value={profile.display_name}
          onChange={(e) => setProfile({ display_name: e.target.value })}
          className="w-full rounded-lg border border-border px-3 py-2 text-sm text-text-primary outline-none transition-colors focus:border-brand focus:ring-1 focus:ring-brand"
          placeholder="Studio"
        />
      </Field>

      {/* Language pair */}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Native language">
          <select
            value={profile.native_language}
            onChange={(e) =>
              setProfile({
                native_language: e.target.value as SourceLanguageCode,
              })
            }
            className="w-full rounded-lg border border-border px-3 py-2 text-sm text-text-primary outline-none transition-colors focus:border-brand focus:ring-1 focus:ring-brand"
          >
            {SOURCE_LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.flag} {l.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Target language">
          <select
            value={profile.target_language}
            onChange={(e) =>
              setProfile({
                target_language: e.target.value as TargetLanguageCode,
              })
            }
            className="w-full rounded-lg border border-border px-3 py-2 text-sm text-text-primary outline-none transition-colors focus:border-brand focus:ring-1 focus:ring-brand"
          >
            {TARGET_LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.flag} {l.name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {/* Level */}
      <Field label="CEFR level">
        <select
          value={profile.level}
          onChange={(e) => setProfile({ level: e.target.value as CefrLevelCode })}
          className="w-full rounded-lg border border-border px-3 py-2 text-sm text-text-primary outline-none transition-colors focus:border-brand focus:ring-1 focus:ring-brand"
        >
          {CEFR_LEVELS.map((l) => (
            <option key={l.code} value={l.code}>
              {l.name}
            </option>
          ))}
        </select>
      </Field>

      {/* Pace */}
      <Field label="Pace">
        <div className="grid grid-cols-4 gap-2">
          {PACE_TIERS.map((p) => (
            <button
              key={p.code}
              onClick={() => setProfile({ pace: p.code as PaceCode })}
              className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                profile.pace === p.code
                  ? 'border-brand bg-brand-light text-brand-dark'
                  : 'border-border text-text-muted hover:border-brand/50'
              }`}
            >
              {p.name}
            </button>
          ))}
        </div>
      </Field>

      {/* Topics */}
      <Field label="Topics">
        <div className="flex flex-wrap gap-2">
          {TOPICS.map((t) => {
            const isSelected = profile.topics.includes(t.code as TopicCode);
            return (
              <button
                key={t.code}
                onClick={() => {
                  const topics = isSelected
                    ? profile.topics.filter((c) => c !== t.code)
                    : [...profile.topics, t.code as TopicCode];
                  setProfile({ topics });
                }}
                className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                  isSelected
                    ? 'border-brand bg-brand-light text-brand-dark'
                    : 'border-border text-text-muted hover:border-brand/50'
                }`}
              >
                {t.emoji} {t.name}
              </button>
            );
          })}
        </div>
      </Field>

      {/* ── Motivation ────────────────────────────────────────────── */}
      <div className="border-t border-border pt-4">
        <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-text-light">
          Motivation
        </p>

        {/* Learning reason */}
        <Field label="Learning reason">
          <select
            value={profile.learning_reason ?? ''}
            onChange={(e) =>
              setProfile({
                learning_reason:
                  e.target.value === '' ? null : (e.target.value as LearningReasonCode),
              })
            }
            className="w-full rounded-lg border border-border px-3 py-2 text-sm text-text-primary outline-none transition-colors focus:border-brand focus:ring-1 focus:ring-brand"
          >
            <option value="">-- None --</option>
            {LEARNING_REASONS.map((r) => (
              <option key={r.code} value={r.code}>
                {r.name}
              </option>
            ))}
          </select>
        </Field>

        {/* Situations */}
        <div className="mt-4">
          <Field label="Situations">
            <div className="flex flex-wrap gap-2">
              {SITUATIONS.map((s) => {
                const isSelected = profile.situations?.includes(s.code as SituationCode);
                return (
                  <button
                    key={s.code}
                    onClick={() => {
                      const current = profile.situations ?? [];
                      const situations = isSelected
                        ? current.filter((c) => c !== s.code)
                        : [...current, s.code as SituationCode];
                      setProfile({
                        situations: situations.length > 0 ? situations : null,
                      });
                    }}
                    className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                      isSelected
                        ? 'border-brand bg-brand-light text-brand-dark'
                        : 'border-border text-text-muted hover:border-brand/50'
                    }`}
                  >
                    {s.emoji} {s.name}
                  </button>
                );
              })}
            </div>
          </Field>
        </div>
      </div>

      {/* ── Your World ─────────────────────────────────────────────── */}
      <div className="border-t border-border pt-4">
        <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-text-light">
          Your World
        </p>

        {/* Profession */}
        <Field label="Profession">
          <input
            type="text"
            value={profile.profession ?? ''}
            onChange={(e) =>
              setProfile({
                profession: e.target.value || null,
              })
            }
            className="w-full rounded-lg border border-border px-3 py-2 text-sm text-text-primary outline-none transition-colors focus:border-brand focus:ring-1 focus:ring-brand"
            placeholder="e.g. Software engineer"
          />
        </Field>

        {/* Destination city + country */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <Field label="Destination city">
            <input
              type="text"
              value={profile.destination_city ?? ''}
              onChange={(e) =>
                setProfile({
                  destination_city: e.target.value || null,
                })
              }
              className="w-full rounded-lg border border-border px-3 py-2 text-sm text-text-primary outline-none transition-colors focus:border-brand focus:ring-1 focus:ring-brand"
              placeholder="e.g. Barcelona"
            />
          </Field>
          <Field label="Destination country">
            <input
              type="text"
              value={profile.destination_country ?? ''}
              onChange={(e) =>
                setProfile({
                  destination_country: e.target.value || null,
                })
              }
              className="w-full rounded-lg border border-border px-3 py-2 text-sm text-text-primary outline-none transition-colors focus:border-brand focus:ring-1 focus:ring-brand"
              placeholder="e.g. Spain"
            />
          </Field>
        </div>

        {/* Learning focus */}
        <div className="mt-4">
          <Field label="Learning focus">
            <div className="flex flex-wrap gap-2">
              {LEARNING_FOCUS.map((f) => {
                const isSelected = profile.learning_focus?.includes(f.code as LearningFocusCode);
                return (
                  <button
                    key={f.code}
                    onClick={() => {
                      const current = profile.learning_focus ?? [];
                      const learning_focus = isSelected
                        ? current.filter((c) => c !== f.code)
                        : [...current, f.code as LearningFocusCode];
                      setProfile({
                        learning_focus: learning_focus.length > 0 ? learning_focus : null,
                      });
                    }}
                    className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                      isSelected
                        ? 'border-brand bg-brand-light text-brand-dark'
                        : 'border-border text-text-muted hover:border-brand/50'
                    }`}
                  >
                    {f.name}
                  </button>
                );
              })}
            </div>
          </Field>
        </div>
      </div>

      {/* Provider selector */}
      <ProviderSelector />

      {/* Cost estimate */}
      <div className="rounded-lg border border-border bg-gray-50 p-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-text-muted">Blocks</span>
          <span className="font-medium">{blockCount}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-text-muted">Pages (est.)</span>
          <span className="font-medium">~{pageCount}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-text-muted">AI cost (est.)</span>
          <span className="font-medium">~${costEstimate.totalEstimatedCostUsd.toFixed(2)}</span>
        </div>
      </div>

      {/* Generate button */}
      <button
        onClick={generate}
        disabled={generating || profile.topics.length === 0}
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-dark disabled:opacity-50"
      >
        {generating ? (
          <>
            <Loader2 size={18} className="animate-spin" />
            Generating...
          </>
        ) : (
          <>
            <Sparkles size={18} />
            Generate Workbook
          </>
        )}
      </button>
    </div>
  );
}

// =============================================================================
// Field wrapper
// =============================================================================

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-text-muted">
        {label}
      </label>
      {children}
    </div>
  );
}
